import { useEffect, useState } from "react";
import Modal from "../../layout/Modal";
import TextInput from "../../layout/TextInput";
import TextArea from "../../layout/TextArea";
import IntegerInput from "../../layout/IntegerInput";
import Select from "../../layout/Select";
import Button from "../../layout/Button";
import useFormValidation from "../../../hooks/formValidation";
import { Queue } from "../../../model/queue.model";
import { WhatsappBotMenuOption } from "../../../model/whatsappBot.model";
import { isDigitAvailable } from "../../../model/whatsappBotMenu.utils";

function MenuOptionModal({
  shown,
  mode,
  option,
  initialDigit,
  existingOptions,
  queues,
  onClose,
  onSave,
}: MenuOptionModalProps) {
  const [digit, setDigit] = useState("");
  const [name, setName] = useState("");
  const [type, setType] = useState<"queue" | "submenu">("queue");
  const [selectedQueueId, setSelectedQueueId] = useState("");
  const [confirmationMessage, setConfirmationMessage] = useState("");

  const formValidation = useFormValidation();

  const currentDigit = option ? option.option : undefined;
  const discardedOptionsCount =
    option?.type === "submenu" ? option.node.options.length : 0;
  const losesSubmenuOptions = discardedOptionsCount > 0 && type === "queue";

  useEffect(() => {
    if (!shown) return;
    setDigit(option ? String(option.option) : String(initialDigit ?? ""));
    setName(option?.name || "");
    setType(option?.type || "queue");
    setSelectedQueueId(option?.type === "queue" ? option.queueId : "");
    setConfirmationMessage(option?.confirmationMessage || "");
  }, [shown, option, initialDigit]);

  const digitValidation = (v: string) => {
    const num = parseInt(v, 10);
    if (isNaN(num) || num < 0 || num > 9) {
      return { error: true, message: "Tecla de 0 a 9" };
    }
    if (!isDigitAvailable(existingOptions, num, currentDigit)) {
      return { error: true, message: "Já utilizado neste nível" };
    }
    return { error: false };
  };

  const onQueuePicked = (queueId: string) => {
    setSelectedQueueId(queueId);
    const picked = queues.find((q) => q.id === queueId);
    if (picked) setName(picked.name);
  };

  const saveHandler = () => {
    if (!formValidation.validateForm()) return;
    const digitNum = parseInt(digit, 10);

    const trimmedConfirmation = confirmationMessage.trim();

    if (type === "submenu") {
      onSave({
        option: digitNum,
        name,
        kind: "submenu",
        ...(trimmedConfirmation ? { confirmationMessage: trimmedConfirmation } : {}),
      });
      return;
    }

    onSave({
      option: digitNum,
      name,
      kind: "queue",
      queueId: selectedQueueId,
      ...(trimmedConfirmation ? { confirmationMessage: trimmedConfirmation } : {}),
    });
  };

  return (
    <Modal
      title={mode === "add" ? "Nova opção" : "Editar opção"}
      className="w-[520px]"
      shown={shown}
      onClose={onClose}
    >
      <div className="grid grid-cols-1 gap-4 mb-6">
        <div className="grid grid-cols-[100px_1fr] gap-4">
          <IntegerInput
            title="Tecla"
            value={digit}
            setter={setDigit}
            formValidation={formValidation}
            validation={digitValidation}
            required
          />
          <TextInput
            title="Nome da opção"
            value={name}
            setter={setName}
            formValidation={formValidation}
            required
          />
        </div>

        <div>
          <div className="text-xs 2xl:text-sm font-bold text-gray-dark mb-2">
            Ao digitar/tocar, o contato...
          </div>
          <div className="flex rounded-10 border-2 border-gray/50 overflow-hidden">
            <button
              type="button"
              className={`flex-1 py-2 text-sm font-bold ${
                type === "queue" ? "bg-green/10 text-green" : "text-gray-dark"
              }`}
              onClick={() => setType("queue")}
            >
              Entra em uma fila
            </button>
            <button
              type="button"
              className={`flex-1 py-2 text-sm font-bold border-l-2 border-gray/50 ${
                type === "submenu"
                  ? "bg-purple/10 text-purple"
                  : "text-gray-dark"
              }`}
              onClick={() => setType("submenu")}
            >
              Abre outro nível de menu
            </button>
          </div>
        </div>

        {losesSubmenuOptions && (
          <div className="bg-red/10 text-red text-xs font-bold px-3 py-2 rounded-10">
            Este submenu tem {discardedOptionsCount} opção(ões)
            configurada(s). Mudar para fila vai descartá-las.
          </div>
        )}

        <div>
          <TextArea
            title="Mensagem de confirmação (opcional)"
            className="w-full"
            value={confirmationMessage}
            setter={setConfirmationMessage}
            formValidation={formValidation}
            charLimit={500}
          />
          <p className="text-gray-dark text-xs mt-1">
            Enviada ao contato assim que ele escolhe esta opção, antes de
            {type === "queue"
              ? " encaminhar pra fila (substitui o aviso padrão de encaminhamento)."
              : " abrir o próximo nível de menu."}
          </p>
        </div>

        {type === "queue" && (
          <Select
            title="Fila"
            className="w-full"
            value={selectedQueueId}
            setter={onQueuePicked}
            //@ts-ignore
            formValidation={formValidation}
            required
          >
            {queues.map((q, i) => (
              <option value={q.id} key={i}>
                {q.name}
              </option>
            ))}
          </Select>
        )}
      </div>
      <div>
        <Button color="purple" className="float-right" onClick={saveHandler}>
          Salvar
        </Button>
      </div>
    </Modal>
  );
}

export default MenuOptionModal;

export type MenuOptionModalResult =
  | { option: number; name: string; kind: "submenu"; confirmationMessage?: string }
  | { option: number; name: string; kind: "queue"; queueId: string; confirmationMessage?: string };

export type MenuOptionModalProps = {
  shown: boolean;
  mode: "add" | "edit";
  option?: WhatsappBotMenuOption;
  initialDigit?: number;
  existingOptions: WhatsappBotMenuOption[];
  queues: Queue[];
  onClose: () => void;
  onSave: (result: MenuOptionModalResult) => void;
};
